import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type { SpecDecisionLog, SpecDecisionRow } from '../../../lib/api/spec-requirements.js';

/** 列と見出しの翻訳キー。module scope では hook を呼べないので鍵で持つ。 @graph-connects none */
const COLUMNS = [
  ['topic', 'spec.decisionLog.columns.topic'],
  ['decision', 'spec.decisionLog.columns.decision'],
  ['rejected', 'spec.decisionLog.columns.rejected'],
  ['why', 'spec.decisionLog.columns.why'],
] as const satisfies readonly (readonly [keyof SpecDecisionRow, string])[];

/** 空の 1 行。 @graph-connects none */
const EMPTY_ROW: SpecDecisionRow = { topic: '', decision: '', rejected: '', why: '' };

/**
 * 保存できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる）。
 * @graph-connects none
 */
function reason(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('spec.error.forbidden');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('spec.error.saveFailed');
}

export interface DecisionLogEditorProps {
  /** 今保存されている決定記録。無い案件では null */
  decisionLog: SpecDecisionLog | null;
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因。文字数上限・未決・記入例はここに API のメッセージが入る */
  error: unknown;
  /** 保存が通ったときに残った注意（行数が目安を超えた・捨てた案が無い行） */
  warnings: string[];
  /** 保存する */
  onSave: (rows: SpecDecisionRow[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/**
 * 決定記録を編集する。
 *
 * 列ごとの文字数上限・空欄・未決の印・記入例の行・決めたことと選ばなかった案が同じ、の判定は
 * API が持つ（`check_decision_log` と同じ規則）。画面は**今の文字数だけ**を出し、超過は保存時に
 * 返るメッセージをそのまま見せる。上限の数を画面に持たせると、変わったときに黙ってずれる。
 *
 * **行数の上限は画面にも API にも無い**（目安を超えると warnings が返るだけ）。決定記録は引くための
 * 表で、決定は案件が進むほど正当に増えるため、行数で塞ぐと記録しないか畳むかになる。
 * だから「行を足す」を止めず、返ってきた警告を「気になるところ」に出すだけにする。
 *
 * 選ばなかった案は実際に検討した案だけを書く。無かったなら「なし」（空欄にしない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 決定記録（決めること・決めたこと・選ばなかった案・なぜ）を表として編集して保存する
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 */
export function DecisionLogEditor({
  decisionLog,
  saving,
  error,
  warnings,
  onSave,
  onCancel,
}: DecisionLogEditorProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SpecDecisionRow[]>(() =>
    decisionLog === null || decisionLog.rows.length === 0 ? [{ ...EMPTY_ROW }] : decisionLog.rows,
  );

  const setCell = (index: number, key: keyof SpecDecisionRow, value: string) => {
    setRows((current) => current.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  };

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        // 全部空の行は送らない（API が空欄をエラーにする。行数自体は目安なので送れる）
        onSave(rows.filter((r) => COLUMNS.some(([key]) => r[key].trim() !== '')));
      }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {COLUMNS.map(([key, labelKey]) => (
                <th
                  key={key}
                  className="text-left text-xs font-semibold text-[var(--color-text-secondary)] pb-1"
                >
                  {t(labelKey)}
                </th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              // 並べ替えではなく入れ替えなので index を鍵にしてよい
              <tr key={index}>
                {COLUMNS.map(([key, labelKey]) => (
                  <td key={key} className="pr-2 pb-2 align-top">
                    <textarea
                      className="block w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm"
                      rows={2}
                      value={row[key]}
                      aria-label={t('spec.section.rowFieldAria', {
                        row: index + 1,
                        field: t(labelKey),
                      })}
                      onChange={(e) => {
                        setCell(index, key, e.target.value);
                      }}
                    />
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {t('spec.charCount', { n: row[key].length })}
                    </span>
                  </td>
                ))}
                <td className="align-top pb-2">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
                    aria-label={t('spec.section.rowRemoveAria', { row: index + 1 })}
                    onClick={() => {
                      setRows((current) => current.filter((_, i) => i !== index));
                    }}
                  >
                    {t('spec.action.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="mt-1 text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
        onClick={() => {
          setRows((current) => [...current, { ...EMPTY_ROW }]);
        }}
      >
        {t('spec.action.addRow')}
      </button>

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(t, error)}
        </p>
      )}

      {warnings.length === 0 ? null : (
        <div className="mt-3 text-sm text-[var(--color-text-primary)]">
          <p className="font-semibold text-[var(--color-warning-text)]">{t('spec.concerns')}</p>
          <ul className="list-disc pl-5">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          {t(saving ? 'spec.action.saving' : 'spec.action.save')}
        </button>
        <button
          type="button"
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={onCancel}
        >
          {t('spec.action.cancel')}
        </button>
      </div>
    </form>
  );
}
